import { capitalize } from 'inflected';
import { abbreviateNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { SuperTopItemType } from '@Components/StickyTable/StickyTable';
import { MIN_CHANGE_THRESHOLD } from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { formatPercentChangeLabel } from '@Components/PercentChange';
import {
  GROUP_BY_ENUM,
  HroasSpModel,
  ScenarioNode
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import {
  Neutral500,
  ScenarioDeltaGreen100,
  ScenarioDeltaGreen200,
  ScenarioDeltaGreen300,
  ScenarioDeltaGreen400,
  ScenarioDeltaRed100,
  ScenarioDeltaRed200
} from '@src/utils/colors';

export { MIN_CHANGE_THRESHOLD };

// Per-value breakdown for one metric × column-type (actualized/induced/direct)
export interface MetricValues {
  newValue: number;
  prevValue: number;
  baselineValue: number;
  newVsPrevPct: number;
  newVsPrevDelta: number;
  prevVsBaselinePct: number;
  prevVsBaselineDelta: number;
}

export const ZERO_METRIC_VALUES: MetricValues = {
  newValue: 0,
  prevValue: 0,
  baselineValue: 0,
  newVsPrevPct: 0,
  newVsPrevDelta: 0,
  prevVsBaselinePct: 0,
  prevVsBaselineDelta: 0
};

export interface DeltaRow {
  name: string;
  hierarchyParts: string[];
  level: number;
  actualizedRev: MetricValues;
  inducedRev: MetricValues;
  directRev: MetricValues;
  actualizedSpend: MetricValues;
  inducedSpend: MetricValues;
  directSpend: MetricValues;
  // ==== HROAS: this row's actualized revenue (direct + net induced), broken out by contributing
  // model instance. Undefined on non-HROAS pages and on HROAS rows with no per-model attribution
  // (e.g. a row with no matching leaf). This is the sum of byModelRevSplit's direct + indirect
  // values below (indirect is already net of reattributedOut there), not an independent source. ====
  byModelRev?: Record<string, MetricValues>;
  // ==== HROAS: same per-model breakdown as byModelRev, but split into direct vs. indirect
  // revenue — sourced from previousByModel/baselineByModel's separate directRevenue/
  // indirectRevenue fields. Brand Equity's revenue is reclassified entirely into Indirect
  // (reclassifyBrandEquityRevenueAsIndirect, ScenarioPlanningUtils.tsx) since its underlying model
  // has no unmediated "direct" term — so BE's Direct column reads $0 and Indirect carries its
  // full contribution, by design, not as a placeholder for missing data. ====
  byModelRevSplit?: Record<string, Record<REVENUE_SPLIT, MetricValues>>;
}

export interface ColumnConfig {
  showActualized: boolean;
  showPctChange: boolean;
  showActualChange: boolean;
  showDirect: boolean;
  showInduced: boolean;
  showPrevScenarioPct: boolean;
  showPrevScenarioDelta: boolean;
  showBaseline: boolean;
  showInducedDetails: boolean;
  // ==== HROAS: toggles the by-model contribution summary panel ====
  showByModel: boolean;
}

export const DEFAULT_COLUMN_CONFIG: ColumnConfig = {
  showActualized: true,
  showPctChange: true,
  showActualChange: true,
  showDirect: false,
  showInduced: false,
  showPrevScenarioPct: false,
  showPrevScenarioDelta: false,
  showBaseline: false,
  showInducedDetails: false,
  showByModel: false
};

export type CompareTarget = 'previous' | 'baseline';

export interface CellTooltipData {
  title: string;
  baseline: number;
  prevValue: number;
  newValue: number;
  prevVsBaselinePct: number;
  newVsBaselinePct: number;
}

// ==== HROAS: direct vs. indirect revenue discriminator for by-model columns ====
export const REVENUE_SPLIT = {
  DIRECT: 'direct',
  INDIRECT: 'indirect'
} as const;
export type REVENUE_SPLIT = (typeof REVENUE_SPLIT)[keyof typeof REVENUE_SPLIT];

export const REVENUE_SPLIT_LABEL: Record<REVENUE_SPLIT, string> = {
  [REVENUE_SPLIT.DIRECT]: 'Direct',
  [REVENUE_SPLIT.INDIRECT]: 'Indirect'
};

export interface ColumnDef {
  groupType: 'actualized' | 'induced' | 'direct';
  metric: 'rev' | 'spend';
  subType: 'pctChange' | 'actualChange' | 'prevPct' | 'prevDelta' | 'baseline' | 'inducedDetail';
  label: string;
  isAbbreviated: boolean;
  getValue: (row: DeltaRow) => number;
  getColorPct: (row: DeltaRow) => number;
  getTooltipData: (row: DeltaRow) => CellTooltipData;
  // ==== HROAS: set only on a by-model crossed column — identifies which model this column's
  // values come from, so the super-top header can label it by model name instead of by
  // groupType. Undefined on every ordinary stage-lens column. revenueSplit further distinguishes
  // the model's Direct vs. Indirect sub-columns — also undefined on ordinary stage-lens
  // columns. ====
  modelId?: string;
  modelLabel?: string;
  revenueSplit?: REVENUE_SPLIT;
}

// ==== HROAS: a model the by-model columns can be generated for (also the SP page's HroasSpModel,
// one entry per run slot). ====
export type ByModelColumnModel = HroasSpModel;

export const LEFT_COLUMN_WIDTH = 220;
export const HEADER_HEIGHT = 32;
export const SUPER_TOP_HEIGHT = 32;
export const MIN_COLUMN_WIDTH = 140;
export const BASE_ROW_HEIGHT = 40;
export const ROW_HEIGHT_PER_LEVEL = 16;

export const DIMENSION_SORT_INDEX = -1;
export const CURRENCY_DECIMALS = 0;
export const ROAS_DECIMALS = 2;
export const CURRENCY_PREFIX = '$';

const GREEN_SCALE = [
  ScenarioDeltaGreen100,
  ScenarioDeltaGreen200,
  ScenarioDeltaGreen300,
  ScenarioDeltaGreen400
];
const RED_SCALE = [ScenarioDeltaRed100, ScenarioDeltaRed200];

export const calcPct = (newVal: number, oldVal: number): number => {
  if (oldVal === 0) {
    if (newVal === 0) {
      return 0;
    }
    return newVal > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  }
  return ((newVal - oldVal) / Math.abs(oldVal)) * 100;
};

export const toPrettyPercent = (pct: number): string =>
  formatPercentChangeLabel(pct, { decimals: 1, collapseSubOne: true });

// Returns 0 when the underlying dollar delta is below the noise floor, so
// percent cells stay grey/"0%" instead of green/"<1%" for sub-$10 changes.
const thresholdPct = (pct: number, dollarDelta: number): number =>
  Math.abs(dollarDelta) < MIN_CHANGE_THRESHOLD ? 0 : pct;

export interface ModelTotals {
  newValue: number;
  oldValue: number;
  baselineValue: number;
}

// Noise-suppresses a per-model total the same way the overview cards suppress theirs: a movement
// under the noise floor (float32 model noise, e.g. -$0.93 on $2.8M for an untouched scenario) is
// treated as no change, so the card reads 0% instead of red "-<1%".
export const suppressSubThresholdModelNoise = ({
  newValue,
  oldValue,
  baselineValue
}: ModelTotals): ModelTotals => {
  const effectiveOld =
    Math.abs(oldValue - baselineValue) < MIN_CHANGE_THRESHOLD ? baselineValue : oldValue;
  // new is snapped against both references, so it never prints a sub-threshold move versus
  // the baseline (even when old, snapped to the baseline, is far from new) or versus old. The
  // snap to old only applies when old kept its own value: when old was snapped to the baseline,
  // snapping new to it would hide a real move versus the baseline (1000 / 1005 / 1012).
  let effectiveNew = newValue;
  if (Math.abs(newValue - baselineValue) < MIN_CHANGE_THRESHOLD) {
    effectiveNew = baselineValue;
  } else if (effectiveOld === oldValue && Math.abs(newValue - oldValue) < MIN_CHANGE_THRESHOLD) {
    effectiveNew = effectiveOld;
  }
  return { newValue: effectiveNew, oldValue: effectiveOld, baselineValue };
};

export const abbreviateValue = (value: number): string => {
  if (Math.abs(value) < MIN_CHANGE_THRESHOLD) {
    return '$0';
  }
  const sign = value > 0 ? '+' : '-';
  return `${sign}$${abbreviateNumber(Math.abs(value), 1)}`;
};

export const getIntensityColor = (pct: number): string => {
  const abs = Math.abs(pct);
  if (abs === 0) {
    return Neutral500;
  }
  if (pct >= 0) {
    if (abs <= 1) {
      return GREEN_SCALE[0];
    }
    if (abs <= 3) {
      return GREEN_SCALE[1];
    }
    if (abs <= 5) {
      return GREEN_SCALE[2];
    }
    return GREEN_SCALE[3];
  }
  if (abs <= 5) {
    return RED_SCALE[0];
  }
  return RED_SCALE[1];
};

export const DIMENSION_COLUMN_WIDTH = 150;

export const getDimensionColumnHeaders = (groupBy: GROUP_BY_ENUM): string[] =>
  groupBy.split(' > ').slice(1);

export const collectLeafEntries = (
  nodes: ScenarioNode[],
  prefix = ''
): { node: ScenarioNode; key: string }[] =>
  nodes.flatMap(node => {
    const key = prefix ? `${prefix}, ${node.name}` : node.name;
    return node.children?.length ? collectLeafEntries(node.children, key) : [{ node, key }];
  });

// Collapse repeated hierarchy parts into "--" so each row only labels each distinct level once.
export const dedupeHierarchyParts = (parts: (string | undefined)[]): string[] => {
  const norm = (s: string | undefined): string => (s ?? '').trim().toLowerCase();
  const isBlank = (s: string | undefined): boolean => !s || s === '--';
  return parts.map((part, i) => {
    if (isBlank(part)) {
      return '--';
    }
    const earlier = parts.slice(0, i);
    return earlier.some(p => !isBlank(p) && norm(p) === norm(part)) ? '--' : (part as string);
  });
};

export const buildMetricValues = (
  newValue: number,
  prevValue: number,
  baselineValue: number
): MetricValues => ({
  newValue,
  prevValue,
  baselineValue,
  newVsPrevPct: calcPct(newValue, prevValue),
  newVsPrevDelta: newValue - prevValue,
  prevVsBaselinePct: calcPct(prevValue, baselineValue),
  prevVsBaselineDelta: prevValue - baselineValue
});

const getMetricField = (
  row: DeltaRow,
  groupType: ColumnDef['groupType'],
  metric: ColumnDef['metric']
): MetricValues => {
  if (groupType === 'actualized') {
    return metric === 'rev' ? row.actualizedRev : row.actualizedSpend;
  }
  if (groupType === 'induced') {
    return metric === 'rev' ? row.inducedRev : row.inducedSpend;
  }
  return metric === 'rev' ? row.directRev : row.directSpend;
};

const makeTooltipData = (title: string, mv: MetricValues): CellTooltipData => {
  const newVsBaselineDelta = mv.newValue - mv.baselineValue;
  return {
    title,
    baseline: mv.baselineValue,
    prevValue: mv.prevValue,
    newValue: mv.newValue,
    prevVsBaselinePct: thresholdPct(mv.prevVsBaselinePct, mv.prevVsBaselineDelta),
    newVsBaselinePct: thresholdPct(calcPct(mv.newValue, mv.baselineValue), newVsBaselineDelta)
  };
};

const activeGroupTypes = (
  config: ColumnConfig,
  isRMMM: boolean
): ('actualized' | 'induced' | 'direct')[] => {
  const groups: ('actualized' | 'induced' | 'direct')[] = [];
  if (config.showActualized) {
    groups.push('actualized');
  }
  if (isRMMM && config.showInduced) {
    groups.push('induced');
  }
  if (config.showDirect) {
    groups.push('direct');
  }
  // Always show at least actualized
  if (groups.length === 0) {
    groups.push('actualized');
  }
  return groups;
};

// The % change for the selected comparison, floored to 0 under the noise threshold. Shared by the
// value and the color of the pctChange/actualChange cells so a color never disagrees with the number.
export const getComparedPct = (mv: MetricValues, compareTo: CompareTarget): number =>
  compareTo === 'baseline'
    ? thresholdPct(calcPct(mv.newValue, mv.baselineValue), mv.newValue - mv.baselineValue)
    : thresholdPct(mv.newVsPrevPct, mv.newVsPrevDelta);

// Shared by addByModelSubCols and buildRMMMColumnDefs's addSubCols — both build the same four
// pctChange/actualChange/prevScenarioPct/prevScenarioDelta column-def shapes, differing only in
// `base`'s fields (groupType/metric vs modelId/modelLabel/revenueSplit) and how `getMV` reads a
// row's MetricValues.
const buildMetricColumnDefs = (
  defs: ColumnDef[],
  config: ColumnConfig,
  compareTo: CompareTarget,
  base: Pick<ColumnDef, 'groupType' | 'metric'> &
    Partial<Pick<ColumnDef, 'modelId' | 'modelLabel' | 'revenueSplit'>>,
  getMV: (row: DeltaRow) => MetricValues,
  title: string
): void => {
  const compareLabel = compareTo === 'baseline' ? 'Baseline' : 'Previous';
  const getTooltipData = (row: DeltaRow): CellTooltipData => makeTooltipData(title, getMV(row));

  if (config.showPctChange) {
    defs.push({
      ...base,
      subType: 'pctChange',
      label: `% Change from ${compareLabel}`,
      isAbbreviated: false,
      getValue: row => getComparedPct(getMV(row), compareTo),
      getColorPct: row => getComparedPct(getMV(row), compareTo),
      getTooltipData
    });
  }
  if (config.showActualChange) {
    defs.push({
      ...base,
      subType: 'actualChange',
      label: `$ Change from ${compareLabel}`,
      isAbbreviated: true,
      getValue: row => {
        const mv = getMV(row);
        return compareTo === 'baseline' ? mv.newValue - mv.baselineValue : mv.newVsPrevDelta;
      },
      getColorPct: row => getComparedPct(getMV(row), compareTo),
      getTooltipData
    });
  }
  if (config.showPrevScenarioPct) {
    defs.push({
      ...base,
      subType: 'prevPct',
      label: 'Previous Scenario % Change',
      isAbbreviated: false,
      getValue: row => thresholdPct(getMV(row).prevVsBaselinePct, getMV(row).prevVsBaselineDelta),
      getColorPct: row =>
        thresholdPct(getMV(row).prevVsBaselinePct, getMV(row).prevVsBaselineDelta),
      getTooltipData
    });
  }
  if (config.showPrevScenarioDelta) {
    defs.push({
      ...base,
      subType: 'prevDelta',
      label: 'Previous Scenario $ Change',
      isAbbreviated: true,
      getValue: row => getMV(row).prevVsBaselineDelta,
      getColorPct: row => getMV(row).prevVsBaselinePct,
      getTooltipData
    });
  }
  if (config.showBaseline) {
    defs.push({
      ...base,
      subType: 'baseline',
      label: 'Baseline',
      isAbbreviated: true,
      getValue: row => getMV(row).baselineValue,
      getColorPct: _row => 0,
      getTooltipData
    });
  }
};

// ==== HROAS: by-model crossed columns, split into Direct/Indirect sub-columns per model —
// byModelRevSplit carries both revenue components separately per model (spend still isn't
// model-specific), so each model gets two runs of columns, one per REVENUE_SPLIT value, both
// always groupType 'actualized' + metric 'rev'. ====
const addByModelSubCols = (
  defs: ColumnDef[],
  config: ColumnConfig,
  compareTo: CompareTarget,
  model: ByModelColumnModel
): void => {
  for (const revenueSplit of [REVENUE_SPLIT.DIRECT, REVENUE_SPLIT.INDIRECT]) {
    const title = `${model.label} - ${REVENUE_SPLIT_LABEL[revenueSplit]}`;
    const getMV = (row: DeltaRow) =>
      row.byModelRevSplit?.[model.id]?.[revenueSplit] ?? ZERO_METRIC_VALUES;

    buildMetricColumnDefs(
      defs,
      config,
      compareTo,
      {
        groupType: 'actualized',
        metric: 'rev',
        modelId: model.id,
        modelLabel: model.label,
        revenueSplit
      },
      getMV,
      title
    );
  }
};

// Builds ColumnDefs for rMMM — Revenue groups first, then Spend
const buildRMMMColumnDefs = (
  config: ColumnConfig,
  compareTo: CompareTarget,
  effectLabel: string,
  byModelModels: ByModelColumnModel[] = []
): ColumnDef[] => {
  const groups = activeGroupTypes(config, true);
  const defs: ColumnDef[] = [];

  const addSubCols = (groupType: 'actualized' | 'induced' | 'direct', metric: 'rev' | 'spend') => {
    const metricLabel = metric === 'rev' ? effectLabel : 'Spend';
    const title = `${capitalize(groupType)} ${metricLabel}`;
    const getMV = (row: DeltaRow) => getMetricField(row, groupType, metric);

    buildMetricColumnDefs(defs, config, compareTo, { groupType, metric }, getMV, title);

    if (groupType === 'induced' && config.showInducedDetails) {
      defs.push({
        groupType,
        metric,
        subType: 'inducedDetail',
        label: 'Induced Value',
        isAbbreviated: true,
        getValue: row => getMV(row).newValue,
        getColorPct: row => getMV(row).newVsPrevPct,
        getTooltipData: row => makeTooltipData(title, getMV(row))
      });
    }
  };

  // Revenue groups first, then Spend (Figma design order)
  for (const g of groups) {
    addSubCols(g, 'rev');
  }
  // ==== HROAS: by-model crossed columns immediately follow the plain Revenue groups — the model
  // lens only ever decomposes revenue (the spend bar stays whole), so there's no by-model Spend
  // run to add. ====
  if (config.showByModel) {
    for (const model of byModelModels) {
      addByModelSubCols(defs, config, compareTo, model);
    }
  }
  for (const g of groups) {
    addSubCols(g, 'spend');
  }
  return defs;
};

// Non-rMMM: preserve original 4-column layout (Spend first, then Revenue, no "Actualized" prefix)
const buildNonRMMMColumnDefs = (effectLabel: string): ColumnDef[] => {
  const makeSimpleCols = (metric: 'rev' | 'spend', label: string): ColumnDef[] => {
    const getMV = (row: DeltaRow) => getMetricField(row, 'actualized', metric);
    const getTooltipData = (row: DeltaRow): CellTooltipData => makeTooltipData(label, getMV(row));
    return [
      {
        groupType: 'actualized',
        metric,
        subType: 'pctChange',
        label: '% Change from Previous',
        isAbbreviated: false,
        getValue: row => thresholdPct(getMV(row).newVsPrevPct, getMV(row).newVsPrevDelta),
        getColorPct: row => thresholdPct(getMV(row).newVsPrevPct, getMV(row).newVsPrevDelta),
        getTooltipData
      },
      {
        groupType: 'actualized',
        metric,
        subType: 'actualChange',
        label: '$ Change from Previous',
        isAbbreviated: true,
        getValue: row => getMV(row).newVsPrevDelta,
        getColorPct: row => getMV(row).newVsPrevPct,
        getTooltipData
      }
    ];
  };
  // Spend first, then Revenue — matching original column order
  return [...makeSimpleCols('spend', 'Spend'), ...makeSimpleCols('rev', effectLabel)];
};

export const buildColumnDefs = (
  config: ColumnConfig,
  isRMMM: boolean,
  compareTo: CompareTarget,
  effectLabel: string,
  byModelModels: ByModelColumnModel[] = []
): ColumnDef[] =>
  isRMMM
    ? buildRMMMColumnDefs(config, compareTo, effectLabel, byModelModels)
    : buildNonRMMMColumnDefs(effectLabel);

// Walk columnDefs in order, grouping consecutive columns by metric for the super-top row.
export const buildSuperTopHeaders = (
  columnDefs: ColumnDef[],
  effectLabel: string
): SuperTopItemType<string>[] => {
  const headers: SuperTopItemType<string>[] = [];
  let i = 0;
  while (i < columnDefs.length) {
    const { metric } = columnDefs[i];
    let j = i;
    while (j < columnDefs.length && columnDefs[j].metric === metric) {
      j++;
    }
    const metricLabel = metric === 'rev' ? effectLabel : 'Spend';
    // ==== HROAS: StickyTable only has room for two header rows (this one + the bottom column
    // labels — there's no third "mid-top" row to put model names in), so a by-model column's
    // model identity has to live here instead. Split the metric run further by modelId, then by
    // revenueSplit (the Direct/Indirect breakout): each model×split combination gets its own
    // span labeled with the model's name plus the split. revenueSplit is undefined on every
    // non-model column, so the added comparison is a no-op there (undefined === undefined); the
    // ordinary (non-model) stage-lens columns keep the original collapse-to-metricLabel-if-mixed-
    // groupTypes behavior, scoped to just that sub-run — identical output to before when no
    // by-model columns exist. ====
    let k = i;
    while (k < j) {
      const { modelId, modelLabel, revenueSplit } = columnDefs[k];
      let count = 0;
      while (
        k < j &&
        columnDefs[k].modelId === modelId &&
        columnDefs[k].revenueSplit === revenueSplit
      ) {
        count++;
        k++;
      }
      if (modelId) {
        // No metricLabel suffix: by-model columns are always revenue (never spend — spend isn't
        // model-specific), and models are free-text labels (e.g. "Brand Equity · Total
        // Revenue") that may already read as revenue on their own, so appending it again reads as
        // a duplicated word rather than useful information.
        const splitSuffix = revenueSplit ? ` - ${REVENUE_SPLIT_LABEL[revenueSplit]}` : '';
        headers.push({ span: count, data: `${modelLabel ?? ''}${splitSuffix}` });
      } else {
        const subDefs = columnDefs.slice(k - count, k);
        const groups = Array.from(new Set(subDefs.map(c => c.groupType)));
        const label = groups.length > 1 ? metricLabel : `${capitalize(groups[0])} ${metricLabel}`;
        headers.push({ span: count, data: label });
      }
    }
    i = j;
  }
  return headers;
};

// Walk columnDefs in order; build mid-top spans per consecutive groupType within each metric section.
// Returns undefined when each metric section has only one group type (no mid-top row needed).
export const buildMidTopHeaders = (
  columnDefs: ColumnDef[]
): SuperTopItemType<string>[] | undefined => {
  const revGroups = Array.from(
    new Set(columnDefs.filter(c => c.metric === 'rev').map(c => c.groupType))
  );
  const spendGroups = Array.from(
    new Set(columnDefs.filter(c => c.metric === 'spend').map(c => c.groupType))
  );
  if (revGroups.length <= 1 && spendGroups.length <= 1) {
    return undefined;
  }

  const headers: SuperTopItemType<string>[] = [];
  let i = 0;
  while (i < columnDefs.length) {
    const { metric } = columnDefs[i];
    let j = i;
    while (j < columnDefs.length && columnDefs[j].metric === metric) {
      j++;
    }
    let k = i;
    while (k < j) {
      const { groupType } = columnDefs[k];
      let count = 0;
      while (k < j && columnDefs[k].groupType === groupType) {
        count++;
        k++;
      }
      headers.push({ span: count, data: capitalize(groupType) });
    }
    i = j;
  }
  return headers;
};

export const buildTopHeaders = (columnDefs: ColumnDef[]): string[] =>
  columnDefs.map(col => col.label);
